<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import Konva from "konva";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { Popup, showLoadingToast, Slider } from "vant";
import { base64ToFile } from "@/modules/common";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import useZoom from "@/components/mobile/generation/custom-painting/useZoom";
import useMoveAndSelection from "@/components/mobile/generation/custom-painting/useMoveAndSelection";
import { AddImageToStageInstance } from "@/components/mobile/generation/custom-painting/AddImageToStage.vue";
import { useMedia } from "@/stores/media";
import { useMate } from "@/stores/mate";
import api from "@/modules/api";
import Stage = Konva.Stage;
import Layer = Konva.Layer;
import Image = Konva.Image;
import i18n from "@/locales";

Konva.hitOnDragEnabled = true;
// Props
const emit = defineEmits<{
  (e: "onFinished", params: Media): void;
}>();

// State
const BACKGROUND_LAYER_NAME = "backgroundLayer";
const show = ref(false);
const initialImageUrl = ref("");
const style = ref(0);
const showStyleSelection = ref(false);
const main = ref<HTMLDivElement | null>(null);
const stage = ref<Stage | null>(null);
const selectionMode = ref<"move" | "stageMove">("move");
const enableStageMove = computed(() => selectionMode.value === "stageMove");
const enableObjectMove = computed(() => selectionMode.value === "move");

const zoomPercent = computed(() => Math.floor(zoomRate.value * 100));
const backgroundLayer = ref<Layer | null>(null); // 背景图片的图层
const layer = ref<Layer | null>(null); // 立绘图片的图层
const imageShapes = ref<Image[]>([]);
const { resetZoom, zoomRate, offset } = useZoom(stage, layer, enableStageMove);
const { clearSelection, activeShapes, transformer } = useMoveAndSelection(
  stage,
  layer,
  enableObjectMove
);
const addImageToStageRef = ref<AddImageToStageInstance | null>(null);
const inpaintingRange = ref(75);

// Lifecycle
watch(selectionMode, (selectionMode) => {
  if (selectionMode !== "move") {
    clearSelection();
  }
});
const prepareKonva = () => {
  // 1. 加载图片，根据图片和容器确定画布的宽高
  // 获取main的宽高
  const { width, height } = main.value!.getBoundingClientRect();
  // 选择最小的边作为画布的边长
  const ratio = 16 / 9;
  let canvasWidth;
  let canvasHeight;
  if (width / height > ratio) {
    canvasHeight = height;
    canvasWidth = height * ratio;
  } else {
    canvasWidth = width;
    canvasHeight = width / ratio;
  }
  // 2. 初始化Konva
  stage.value = new Konva.Stage({
    container: "custom-painting-stage",
    width: canvasWidth,
    height: canvasHeight,
    draggable: false,
  });
  const bgLayer = new Konva.Layer({
    name: BACKGROUND_LAYER_NAME,
    draggable: false,
  });
  stage.value?.add(bgLayer);
  backgroundLayer.value = bgLayer;
  const bgRect = new Konva.Rect({
    x: 0,
    y: 0,
    width: stage.value.width(),
    height: stage.value.height(),
    fill: "#fff",
    draggable: false,
  });
  bgLayer.add(bgRect);
  const characterLayer = new Konva.Layer();
  stage.value?.add(characterLayer);
  layer.value = characterLayer;
};
watch(
  show,
  (show) => {
    if (!show) {
      resetStage();
    } else {
      nextTick(() => {
        const loading = showLoadingToast({
          message: i18n.global.t("Message.loading"),
        });
        prepareKonva();
        loading.close();
      });
    }
  },
  { flush: "post" }
);

watch(
  [show, addImageToStageRef],
  ([show, addImageToStageRef]) => {
    if (!show || !addImageToStageRef || !initialImageUrl.value) return;
    addImageToStageRef.addImage(initialImageUrl.value);
  },
  { flush: "post" }
);

onBeforeUnmount(() => {
  resetStage();
});

const resetStage = () => {
  stage.value?.destroy();
  stage.value = null;
  layer.value = null;
  imageShapes.value = [];
  initialImageUrl.value = "";
  resetZoom();
};
// Event Handler
const clear = () => {
  imageShapes.value = [];
  clearSelection();
  layer.value?.find("Shape").forEach((s) => s.remove());
};

const mediaStore = useMedia();
const mateStore = useMate();
const onConfirm = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.redrawing"),
  });
  try {
    const targetWidth = 1024;
    const targetHeight = 576;
    const exportStage = stage.value?.clone({
      width: targetWidth,
      height: targetHeight,
    });
    const scaleX = targetWidth / stage.value!.width();
    const scaleY = targetHeight / stage.value!.height();
    exportStage?.find("Transformer").forEach((item) => item.destroy());
    exportStage
      ?.getLayers()
      .find((item) => item.name() === BACKGROUND_LAYER_NAME)
      ?.destroy();
    exportStage?.find("Image").forEach((item) => {
      item.x(item.x() * scaleX);
      item.y(item.y() * scaleY);
      item.scaleX(item.scaleX() * scaleX);
      item.scaleY(item.scaleY() * scaleY);
    });
    exportStage?.batchDraw();
    const base64 = exportStage?.toDataURL({ mimeType: "image/png" })!;
    const file = base64ToFile(
      base64,
      `${new Date().toLocaleString().replace(/\D/g, "")}.png`
    );
    const media = await mediaStore.uploadMediaItem(
      { file },
      mateStore.currentMate?.id,
      7
    );
    const { data } = await api.inpaintImage({
      mediaId: media.mediaId,
      imageStyle: style.value,
      prompt: "",
      type: 8,
      strength: parseFloat((inpaintingRange.value / 100).toFixed(2)),
    });
    emit("onFinished", data);
    showStyleSelection.value = false;
    show.value = false;
  } finally {
    loadingToast.close();
  }

  // show.value = false;
  // exportStage.destroy();
};

const start = async (url?: string, initialStyle = 1) => {
  if (url) {
    initialImageUrl.value = url;
  }
  show.value = true;
  style.value = initialStyle;
};

export interface CustomPaintingEditorInstance {
  start: (url: string, initialStyle?: number) => void;
}

defineExpose({
  start,
});
</script>
<template>
  <popup
    id="painting-overlay"
    :closeable="false"
    :show="show"
    class="grid grid-cols-[1fr] bg-base-100 h-full w-full"
    position="top"
  >
    <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
      <threads-nav :show-center="false">
        <template #left>
          <button class="btn btn-ghost" @click="show = false">
            <light-arrow-icon />
          </button>
        </template>
        <template #center>
          <button class="btn btn-xs btn-neutral" @click="resetZoom">
            {{ $t("Texts.zoom") }}: {{ zoomPercent }}%,
            {{ $t("Texts.coordinate") }}: ({{ Math.floor(offset.x) }},
            {{ Math.floor(offset.y) }})
          </button>
        </template>
        <template #right>
          <button
            class="btn btn-sm btn-primary"
            @click="showStyleSelection = true"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </template>
      </threads-nav>
      <div
        ref="main"
        class="overflow-hidden bg-neutral relative flex items-center justify-center"
      >
        <div id="custom-painting-stage" class="bg-base-100"></div>
        <div
          class="flex items-center gap-4 flex-col absolute bg-base-100 right-0 top-0 p-4 border border-primary border-t-0"
        >
          <span class="text-xs">{{ $t("Texts.redrawWidth") }}</span>
          <slider
            v-model="inpaintingRange"
            :max="100"
            :min="0"
            :step="5"
            class="w-44"
            size="sm"
          >
            <template #button>
              <button class="btn btn-circle btn-xs">
                {{ inpaintingRange }}
              </button>
            </template>
          </slider>
        </div>
        <stage-operation-panel
          v-if="stage && layer"
          v-model:active-shapes="activeShapes"
          v-model:shapes="imageShapes"
          :layer="layer"
          :stage="stage"
          :transformer="transformer"
          class="absolute bg-base-100 bottom-0 left-0 w-[20rem] h-40"
        />
      </div>
      <div class="flex gap-2 bg-base-200 p-2 w-full justify-between">
        <div class="flex gap-2">
          <button
            :class="{ 'btn-primary': enableObjectMove }"
            class="btn btn-xs"
            @click="selectionMode = 'move'"
          >
            <i class="iconfont icon-location-arrow -rotate-90" />
          </button>
          <button
            :class="{ 'btn-primary': enableStageMove }"
            class="btn btn-xs"
            @click="selectionMode = 'stageMove'"
          >
            <i class="iconfont icon-hand" />
          </button>
        </div>
        <div class="flex gap-2">
          <add-image-to-stage
            v-if="stage && layer"
            ref="addImageToStageRef"
            v-model:imageShapes="imageShapes"
            :layer="layer"
            :media-select-opt="{
              enablePortraitGallery: true,
              multiple: true,
              maxCount: 10,
            }"
            :stage="stage"
          >
            <button class="btn btn-xs btn-neutral">
              <i class="iconify mingcute--pic-line" />
              {{ $t("Buttons.placePicture") }}
            </button>
          </add-image-to-stage>
        </div>
        <div class="flex gap-2">
          <button
            :disabled="imageShapes.length === 0"
            class="btn btn-xs btn-neutral"
            @click="clear"
          >
            {{ $t("Buttons.clear") }}
          </button>
        </div>
      </div>
    </div>
  </popup>
  <popup
    v-model:show="showStyleSelection"
    class="grid grid-rows-[auto_1fr] h-[80%] overflow-hidden"
    position="bottom"
    teleport="body"
  >
    <threads-nav
      :go-back="
        () => {
          showStyleSelection = false;
        }
      "
      show-back
    >
      <template #center>
        <div></div>
      </template>
      <template #right>
        <button
          :disabled="!style"
          class="btn btn-primary btn-sm"
          @click="onConfirm"
        >
          {{ $t("Buttons.confirm") }}
        </button>
      </template>
    </threads-nav>
    <div class="p-2 h-full overflow-y-auto">
      <image-style-selection v-model:selected-style-id="style" />
    </div>
  </popup>
</template>
<style scoped></style>
